import React, {
  useRef,
  useEffect,
  useState,
  useMemo,
  Fragment,
  forwardRef,
} from "react";
import Chase from "./headwall-components/chase";
import { useServiceStore } from "store";
import { GRAINS, pixelPerInch } from "constants/appConstant";
import Wrapper from "./headwall-components/wrapper";
import { HorizontalRuler, HospitalBed } from "components";
import {
  alterImageUrl,
  calculateHeight,
  calculateWidth,
} from "config/calculations/calculation";
import { HospitalBedIcon } from "assets/images";

const chasePosition = {
  left: "items-start",
  center: "items-center",
  right: "items-end",
};

const manageChasePosition = {
  center: "w-full",
};

const nonDroppableArea = {
  no: "non_dropable_without_chase",
  left: "non_dropable_left_chase",
  right: "non_dropable_right_chase",
  center: "non_dropable_without_chase",
};

const rail_position = {
  middle: "rail_center",
  top: "rail_top",
  bottom: "rail_bottom",
};

const rail_position_margin = {
  middle: "rail_center_margin",
  top: "rail_top_margin",
  bottom: "rail_bottom_margin",
};

const InfinityPanel = forwardRef((props, ref) => {
  const {
    values,
    ruler,
    handleDragStart,
    handleonDragOver,
    handleDrop,
    handleDragEnter,
    dragEntered,
    wallLength,
    clickArrowRight,
    clickArrowLeft,
    service,
    showBedGraphic,
  } = props;

  const wrapperRef = useRef(null);
  const { serv, view_only, setService } = useServiceStore();
  const heightRef = useRef(null);
  const [chaseHeight, setChaseHeight] = useState(0);
  const [customEvent, setCustomEvent] = useState(null);
  const [railInd, setRailInd] = useState(0);

  const {
    chase_color,
    chase_position,
    chased,
    headwalls,
    width,
    add_rail,
    chase_background,
    ceiling_height,
    model,
    accessory_rails,
  } = values;

  const handleKeyDown = (e) => {
    switch (e.key) {
      case "ArrowUp":
        break;
      case "ArrowDown":
        break;
      case "ArrowLeft":
        view_only && clickArrowLeft(customEvent, serv, railInd, 0, "infinity");
        break;
      case "ArrowRight":
        view_only && clickArrowRight(customEvent, serv, railInd, 0, "infinity");
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    let isDragging = false;

    const handleClickOutside = (e) => {
      const clickedInsideFlexItemsEnd = e.target.closest(
        ".flex.items-end".split(" ").join(".")
      );
      if (
        !isDragging &&
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target) &&
        clickedInsideFlexItemsEnd == null
      ) {
        setService("", false);
      }
    };

    const handleDragStart = () => (isDragging = true);
    const handleDragEnd = () => (isDragging = false);

    window.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside); // fixed
    document.addEventListener("dragstart", handleDragStart);
    document.addEventListener("dragend", handleDragEnd);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside); // match above
      document.removeEventListener("dragstart", handleDragStart);
      document.removeEventListener("dragend", handleDragEnd);
    };
  }, [serv, view_only]);

  useEffect(() => {
    const handleChaseHeight = () => {
      const lastChild = heightRef?.current?.lastElementChild;
      // Get the second last child element
      const secondLastChild = lastChild.previousElementSibling;
      // if ruler is added then select second last child other wise last child
      const finalChild = ruler ? secondLastChild : lastChild;
      const outerDivOffsetTop = heightRef.current?.offsetTop;
      const childTopOffset = finalChild?.offsetTop - outerDivOffsetTop;

      // Calculate the height until the child element
      const height = childTopOffset + finalChild?.offsetHeight;
      return height;
    };
    const height = handleChaseHeight();
    setChaseHeight(height);
  }, [
    add_rail,
    headwalls,
    chase_position,
    chased,
    width,
    ruler,
    ceiling_height,
    serv,
    view_only,
  ]);

  //adding some extra pixels in outer div to handle scroll issue while for left and right chase
  const widthCaluclation = useMemo(() => {
    // return Math.round((width.ft * 12 + width.in) / 0.056) + 200;
    if (!chased)
      return Math.round((width.ft * 12 + width.in) / pixelPerInch) + 100;
    else return Math.round((width.ft * 12 + width.in) / pixelPerInch) + 950;
  }, [width, chased]);

  //chase width calculation
  const railWidth = useMemo(() => {
    const sectionWidth = width.ft * 12 + width.in;
    if (chased) {
      if (chase_position === "center") return sectionWidth / pixelPerInch + 250;
      else return (sectionWidth - 0.76) / pixelPerInch; //1.25 for left and right border
    } else return (sectionWidth - 1.25) / pixelPerInch; //1.25 for left and right border
  }, [chased, chase_position, width]);

  const serviceWidth = (ser) => {
    return ser.gang_width / pixelPerInch;
  };

  const servicePosition = (ser) => {
    if (ser?.is_slide) return `translate(${ser.positions?.x}px, ${80}px)`;
    else return `translate(${ser.positions?.x}px, ${0}px)`;
  };

  const generateStyle = (ser) => {
    const styleObj = {
      cursor: "grab",
      background:
        ser == serv ? (view_only ? "#75060f" : "transparent") : "transparent",
    };
    const isSafari =
      /Safari/.test(navigator.userAgent) &&
      /Apple Computer/.test(navigator.vendor);
    if (isSafari) {
      styleObj.left = ser.positions.x;
      styleObj.top = ser?.is_slide ? "80px" : "0px";
    } else styleObj.transform = servicePosition(ser);
    return styleObj;
  };

  const wallHeight = useMemo(() => {
    return Math.round(
      (ceiling_height.ft * 12 + ceiling_height.in) / pixelPerInch
    );
  }, [ceiling_height]);

  useEffect(() => {
    //to adjust scale for axiom
    // Select all elements with the class 'scale'
    if (ruler) {
      const firstElements = document.querySelector(".first");
      const nextElements = document.querySelectorAll(".next");

      // Update styles for 'first' elements
      firstElements.style.maxWidth = "220.285714285px";

      nextElements.forEach((element) => {
        element.style.maxWidth = "214.285714285px";
      });
      // Cleanup function to revert styles when component unmounts
      return () => {
        firstElements.style.maxWidth = "";
        nextElements.forEach((element) => {
          element.style.maxWidth = "";
        });
      };
    }
  }, [ruler, width, pixelPerInch]);

  const rulerWidth = useMemo(() => {
    //added 2 ft for bed
    let totalFeet = 0;
    let totalInches = 0;
    if (chase_position === "center") {
      totalFeet = width.ft + 1; //adding 14 inches to manage center chase of 14 inches
      totalInches = width.in + 2; //adding 14 inches to manage center chase of 14 inches
      if (totalInches >= 12) {
        totalFeet += Math.floor(totalInches / 12);
        totalInches = totalInches % 12;
      }
    } else {
      totalFeet = width.ft;
      totalInches = width.in;
    }

    return { ft: totalFeet, in: totalInches };
  }, [width, chase_position]);

  const accessoriePosition = (acc) => {
    const xPosition = acc.positions?.x;
    if (!xPosition || acc.mount_type !== "track") return xPosition;

    const railWidth = (width.ft * 12 + width.in) / pixelPerInch;
    const chaseWidth = 250;

    // 1. Adjustment for "Single (3 hook)"
    if (acc.name === "Single (3 hook)") {
      return xPosition - 30;
    }

    // Handle "Dual (6 hook)"
    if (acc.name === "Dual (6 hook)") {
      return xPosition - 22;
    }

    // 2. Adjustments for chased configurations
    if (chased) {
      if (acc.name === "Ambu Bag Holder") {
        if (chase_position === "center") {
          const totalWidth = railWidth + chaseWidth;
          const panelMidPoint = totalWidth / 2;

          if (xPosition > panelMidPoint) {
            return xPosition + 9;
          } else if (xPosition < panelMidPoint) {
            return xPosition + 18;
          }
        } else if (chase_position === "right") {
          const chaseMidPoint = railWidth + chaseWidth / 2;

          if (xPosition > chaseMidPoint) {
            return xPosition - 10;
          }
        } else if (chase_position === "left") {
          const chaseMidPoint = chaseWidth / 2;

          if (xPosition < chaseMidPoint) {
            return xPosition + 12;
          }
        }
      }

      // 2. Adjustments for hooks
      if (acc.name === "J Hook") {
        if (chase_position === "center") {
          const totalWidth = railWidth + chaseWidth;
          const panelMidPoint = totalWidth / 2;

          if (xPosition < panelMidPoint) return xPosition + 11;
          else if (xPosition > panelMidPoint) return xPosition + 2;
        } else if (chase_position === "right") {
          const chaseMidPoint = railWidth + chaseWidth / 2;

          if (xPosition < chaseMidPoint) return xPosition - 8;
          else if (xPosition > chaseMidPoint) return xPosition - 19;
        } else if (chase_position === "left") {
          const chaseMidPoint = chaseWidth / 2;

          if (xPosition < chaseMidPoint) return xPosition + 4;
          else if (xPosition > chaseMidPoint) return xPosition - 5;
        }
      }
      //adjustment for clips
      if (acc.name === "Single") {
        if (chase_position === "center") {
          const totalWidth = railWidth + chaseWidth;
          const panelMidPoint = totalWidth / 2;

          if (xPosition < panelMidPoint) return xPosition + 4;
          else if (xPosition > panelMidPoint) return xPosition - 4;
        } else if (chase_position === "right") {
          const chaseMidPoint = railWidth + chaseWidth / 2;

          if (xPosition < chaseMidPoint) return xPosition - 16;
          else if (xPosition > chaseMidPoint) return xPosition - 23;
        } else if (chase_position === "left") {
          const chaseMidPoint = chaseWidth / 2;

          if (xPosition < chaseMidPoint) return xPosition - 4;
          else if (xPosition > chaseMidPoint) return xPosition - 11;
        }
      }

      if (acc.name === "Triple") {
        if (chase_position === "right") {
          const chaseMidPoint = railWidth + chaseWidth / 2;

          if (xPosition < chaseMidPoint) return xPosition - 5;
          else if (xPosition > chaseMidPoint) return xPosition - 15;
        }
      }
      if (acc.name === "Monitor Slide") {
        if (chase_position === "center") {
          const totalWidth = railWidth + chaseWidth;
          const panelMidPoint = totalWidth / 2;

          if (xPosition < panelMidPoint) return xPosition + 15;
        } else if (chase_position === "right") {
          const chaseMidPoint = railWidth + chaseWidth / 2;

          if (xPosition < chaseMidPoint) return xPosition - 8;
          else if (xPosition > chaseMidPoint) return xPosition - 18;
        }
      }

      if (
        acc.category_name === "Glove Box Holder" ||
        acc.category_name === "Baskets"
      ) {
        if (chase_position === "right") {
          return xPosition - 10;
        }
      }

      if (
        acc.name === "12in Pole (3 hook)" ||
        acc.name === "24in Pole (3 hook)"
      ) {
        if (chase_position === "center") {
          const totalWidth = railWidth + chaseWidth;
          const panelMidPoint = totalWidth / 2;

          if (xPosition < panelMidPoint) {
            return xPosition + 10;
          }
        } else if (chase_position === "right") {
          const chaseMidPoint = railWidth + chaseWidth / 2;

          if (xPosition < chaseMidPoint) {
            return xPosition - 10;
          } else if (xPosition > chaseMidPoint) {
            return xPosition - 18;
          }
        }
      }
    }

    // 3. Default fallback for track-mounted accessories
    return xPosition;
  };

  return (
    <>
      <Wrapper
        ruler={ruler}
        width={widthCaluclation}
        wallHeight={wallHeight}
        wallLength={wallLength}
        railWidth={railWidth}
        ceiling_height={ceiling_height}
        model={model}
        chase_position={chase_position}
        ref={ref}
      >
        <div
          className={`h-full flex justify-center ${
            wallLength > railWidth + 20 ? "justify-center" : ""
          }`}
        >
          <div
            id="infinity-board"
            className={`h-full flex relative
            ${!chased ? "justify-center" : ""}
            ${
              chase_position === "center"
                ? `${chasePosition[chase_position]} flex-col`
                : ""
            }
            ${
              dragEntered &&
              (service?.service?.is_accessory_rail ||
                service?.service?.mount_type === "direct") &&
              "bg-green-100"
            }
            `}
            onDragEnter={handleDragEnter}
            onDragOver={(e) => handleonDragOver(e, "rail")}
            onDrop={(e) =>
              handleDrop(
                e,
                service?.service?.is_accessory ||
                  service?.service?.is_accessory_rail
                  ? "rail"
                  : "wall"
              )
            }
          >
            {/* safe ,margin */}
            {dragEntered &&
              (service?.service?.is_accessory_rail ||
                service?.service.mount_type === "direct") && (
                <>
                  <div className="infinty_bottom_accessorie_rail_margin w-full" />
                  <div className="rail_center_margin w-full" />
                  {headwalls.some((hw) => hw.position === "bottom") && (
                    <div className="rail_bottom_margin w-full" />
                  )}
                </>
              )}
            {/* --------------- */}

            {Object.keys(accessory_rails)?.map((railKey, i) => {
              const railData = accessory_rails[railKey] || [];
              return (
                <>
                  {railKey !== "direct" &&
                    railData?.map((ser, i) => (
                      <div
                        draggable
                        key={i}
                        className={`absolute flex justify-center z-10`}
                        style={{
                          left: ser.positions?.x,
                          top: ser.positions?.y,
                          cursor: "grab",
                        }}
                        onDragStart={(e) => handleDragStart(ser, e)}
                        onDragEnter={handleDragEnter}
                        onDrop={(e) => handleDrop(e, "rail")}
                        onDragOver={(e) => handleonDragOver(e, "rail")}
                        // onDragEnter={(e) => e.stopPropagation()}
                      >
                        {dragEntered &&
                          service?.service?.u_id !== ser?.u_id &&
                          service?.service?.is_accessory_rail &&
                          ser.mount_type !== "direct" && (
                            <div
                              className="h-[234.815714286px] top-[-107.142857143px] non_dropable w-full absolute"
                              style={{
                                background: "rgb(215 176 162)",
                                zIndex: 0,
                              }}
                            />
                          )}
                        <div
                          draggable={"false"}
                          style={{
                            width: ser.width / pixelPerInch,
                            backgroundImage: `url(${ser.image_url})`,
                            backgroundSize:
                              ser.width < 15 ? "cover" : "contain",
                            backgroundPosition: "center",
                            pointerEvents: "none",
                            height: ser.height / pixelPerInch,
                            zIndex: 1,
                          }}
                          onDragEnter={(e) => e.stopPropagation()}
                        />
                      </div>
                    ))}
                </>
              );
            })}
            {/* maping accessories */}
            {Object.keys(accessory_rails)?.map((railKey, i) => {
              const railData = accessory_rails[railKey] || [];
              return (
                <>
                  {railData?.map((ser, i) => (
                    <>
                      {ser.accessories?.map((acc, i) => (
                        <div
                          draggable
                          key={i}
                          className="absolute flex justify-center z-10"
                          style={{
                            left: acc.positions?.x,
                            top: acc.positions?.y,
                            cursor: "grab",
                          }}
                          onDragStart={(e) => handleDragStart(acc, e)}
                          onDragEnter={(e) => handleDragEnter(e)}
                          onDrop={(e) => handleDrop(e, "rail")}
                          onDragOver={(e) => handleonDragOver(e, "rail")}
                        >
                          <div
                            draggable={"false"}
                            style={{
                              width: acc.width / pixelPerInch,
                              backgroundImage: `url(${acc.image})`,
                              backgroundSize: "contain",
                              backgroundRepeat: "no-repeat",
                              backgroundPosition: "center",
                              pointerEvents: "none",
                              height: acc.height / pixelPerInch,
                            }}
                            onDragEnter={(e) => e.stopPropagation()}
                          />
                        </div>
                      ))}
                    </>
                  ))}
                </>
              );
            })}
            {/* maping direct*/}
            {accessory_rails["direct"]?.map((acc, i) => (
              <div
                draggable
                key={i}
                className="absolute flex justify-center"
                style={{
                  left: accessoriePosition(acc),
                  top: acc.positions?.y,
                  cursor: "grab",
                  zIndex: 99,
                }}
                onDragStart={(e) => handleDragStart(acc, e)}
                onDragEnter={(e) => handleDragEnter(e)}
                onDrop={(e) => handleDrop(e, "rail")}
                onDragOver={(e) => handleonDragOver(e, "rail")}
              >
                <div
                  draggable={"false"}
                  style={{
                    width: calculateWidth(acc),
                    backgroundImage: `url(${acc.image})`,
                    backgroundSize: "contain",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                    pointerEvents: "none",
                    height: calculateHeight(acc),
                  }}
                  onDragEnter={(e) => e.stopPropagation()}
                />
              </div>
            ))}
            {chased &&
            (chase_position === "left" || chase_position === "center") ? (
              <Chase
                chase_color={chase_color}
                background_image={GRAINS[chase_background]}
                height={chaseHeight}
                chase_position={chase_position}
              />
            ) : null}
            <div
              className={`flex flex-col h-full relative justify-end ${
                chased ? manageChasePosition[chase_position] : " w-full"
              } `}
              ref={heightRef}
              style={{
                width:
                  chase_position === "center" || !chased
                    ? railWidth + 10
                    : railWidth,
                height:
                  (ceiling_height.ft * 12 + ceiling_height.in) / pixelPerInch,
              }}
            >
              <>
                {headwalls?.map((rail, railIndex) => (
                  <Fragment key={railIndex}>
                    {/* ----safe margins  */}
                    {dragEntered &&
                      (service?.service?.is_accessory_rail ||
                        service?.service.mount_type === "direct") && (
                        <>
                          <div
                            className={`infinty_top_accessorie_rail_margin w-full`}
                            style={{
                              bottom:
                                (ceiling_height.ft * 12 + ceiling_height.in) /
                                  pixelPerInch -
                                107.142857143, // to manage 6in height from bottom
                            }}
                          />

                          <div
                            className={`${
                              rail_position_margin[rail.position]
                            } w-full `}
                          />
                          {/* <div className="infinty_bottom_accessorie_rail_margin w-full" /> */}
                        </>
                      )}
                    {/* ------------------------- */}
                    <div
                      className={`w-max headwall_outer ${
                        rail_position[rail.position]
                      } 
                    ${
                      chased && chase_position === "left"
                        ? "chase_left_headwall_outer"
                        : chase_position === "right"
                        ? "chase_right_headwall_outer"
                        : ""
                    }`}
                      onDrop={(e) => handleDrop(e, "rail", railIndex)}
                      onDragOver={(e) => handleonDragOver(e, "rail")}
                      onDragEnter={handleDragEnter}
                    >
                      <div className="headwall">
                        <div
                          className={`relative h-[91.9642857143px] flex w-full ${
                            dragEntered &&
                            !service.service.is_accessory_rail &&
                            !service.service.is_accessory
                              ? nonDroppableArea[chase_position]
                              : ""
                          }`}
                          style={{
                            backgroundColor: rail?.color,
                            width: railWidth,
                            backgroundImage: `url(${GRAINS[rail?.background]})`,
                          }}
                          onDrop={(e) => handleDrop(e, "rail", railIndex)}
                          onDragOver={(e) => handleonDragOver(e, "rail")}
                        >
                          {chased &&
                            dragEntered &&
                            (service?.service?.is_accessory_rail ||
                              service?.service.mount_type === "direct") &&
                            chase_position === "center" && (
                              <div
                                className="chase_center"
                                onDragEnter={(e) => e.stopPropagation()}
                              ></div>
                            )}
                          {rail.services.map((ser, serviceIndex) => (
                            <Fragment key={serviceIndex}>
                              <div
                                ref={wrapperRef}
                                draggable={true}
                                draggableid={ser._id}
                                className={`service_main absolute py-1 flex justify-center h-full items-center ${
                                  dragEntered &&
                                  !service.service.is_accessory_rail &&
                                  !service.service.is_accessory
                                    ? "non_dropable "
                                    : ""
                                }`}
                                style={generateStyle(ser)}
                                onDragStart={(e) => {
                                  handleDragStart(
                                    ser,
                                    e,
                                    railIndex,
                                    serviceIndex
                                  );
                                }}
                                onDragEnter={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                  const rect = e.target.getBoundingClientRect();
                                  setCustomEvent(e);
                                  setService(ser, true);
                                  setRailInd(railIndex);
                                }}
                              >
                                <div
                                  draggable={"false"}
                                  className="service_img pointer-events-none"
                                  style={{
                                    width: serviceWidth(ser),
                                    backgroundImage: `url(${alterImageUrl(
                                      ser.image_url
                                    )})`,
                                    backgroundSize: "contain",
                                    backgroundRepeat: "no-repeat",
                                    backgroundPosition: "center",
                                  }}
                                  onDragEnter={(e) => e.stopPropagation()}
                                />
                              </div>
                            </Fragment>
                          ))}
                        </div>
                      </div>
                    </div>
                  </Fragment>
                ))}

                {/* <div className="w-full  flex justify-center relative ">
                  <img src={HospitalBedIcon} alt="...aa" />
                </div> */}
                {ruler && (
                  <HorizontalRuler
                    class_name={"w-full bottom-[14px] z-50"}
                    width={rulerWidth}
                  />
                )}
              </>
            </div>
            {chased && chase_position === "right" ? (
              <Chase
                chase_color={chase_color}
                background_image={GRAINS[chase_background]}
                height={chaseHeight}
                chase_position={chase_position}
              />
            ) : null}
          </div>

          {showBedGraphic && <HospitalBed />}
        </div>
      </Wrapper>
    </>
  );
});

export default InfinityPanel;
